"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export interface TimePickerProps {
  className?: string;
  default_hour?: number;
  default_minute?: number;
  onTimeChange?: (time: { hour: number; minute: number }) => void;
  triggerId?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Slots strictly before this time are hidden (e.g. end time must follow start). */
  minHour?: number;
  minMinute?: number;
  /** When set, each slot shows its duration from this time ("30 min", "1 h 30"). */
  referenceTime?: { hour: number; minute: number };
  /** Minutes between suggested slots. Typed values can be any minute. */
  step?: number;
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function formatTime(total: number): string {
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} h` : `${h} h ${pad(m)}`;
}

/**
 * Accepts "9", "09", "930", "0930", "9:30", "9h30", "9.30", "9 30", "14h".
 * Returns minutes since midnight, or null when the input is not a valid time.
 */
function parseTime(raw: string): number | null {
  const s = raw.trim().toLowerCase();
  if (!s) return null;

  let h: number;
  let m: number;
  const sep = s.match(/^(\d{1,2})\s*[:h.\s]\s*(\d{0,2})$/);
  if (sep) {
    h = Number(sep[1]);
    m = sep[2] ? Number(sep[2].padEnd(2, "0")) : 0;
  } else if (/^\d{1,4}$/.test(s)) {
    if (s.length <= 2) {
      h = Number(s);
      m = 0;
    } else {
      h = Number(s.slice(0, s.length - 2));
      m = Number(s.slice(-2));
    }
  } else {
    return null;
  }

  if (h > 23 || m > 59) return null;
  return h * 60 + m;
}

export function TimePicker({
  className,
  onTimeChange,
  default_hour,
  default_minute,
  triggerId,
  disabled = false,
  placeholder,
  minHour,
  minMinute,
  referenceTime,
  step = 15,
}: TimePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const value =
    default_hour !== undefined ? default_hour * 60 + (default_minute ?? 0) : undefined;
  const [text, setText] = useState(value !== undefined ? formatTime(value) : "");

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the text in sync with the controlled value (but never while the user is typing).
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setText(value !== undefined ? formatTime(value) : "");
    }
  }, [value]);

  const min = minHour !== undefined ? minHour * 60 + (minMinute ?? 0) : undefined;
  const ref = referenceTime ? referenceTime.hour * 60 + referenceTime.minute : undefined;

  const slots = useMemo(() => {
    const out: number[] = [];
    for (let v = 0; v < 24 * 60; v += step) {
      if (min === undefined || v > min) out.push(v);
    }
    return out;
  }, [step, min]);

  // Slot to highlight/scroll to: the typed value if parseable, else the current value,
  // else the first slot after `min`, else 09:00.
  const typed = parseTime(text);
  const focusTarget = typed ?? value ?? (min !== undefined ? min + step : 9 * 60);
  const nearestSlot = slots.reduce<number | undefined>(
    (best, s) => (best === undefined || Math.abs(s - focusTarget) < Math.abs(best - focusTarget) ? s : best),
    undefined
  );

  useLayoutEffect(() => {
    if (!open || nearestSlot === undefined) return;
    // Wait one frame so the portal content is laid out.
    const id = requestAnimationFrame(() => {
      const el = listRef.current?.querySelector<HTMLElement>(`[data-slot="${nearestSlot}"]`);
      el?.scrollIntoView({ block: "center" });
    });
    return () => cancelAnimationFrame(id);
  }, [open, nearestSlot]);

  const commit = (total: number) => {
    setText(formatTime(total));
    onTimeChange?.({ hour: Math.floor(total / 60), minute: total % 60 });
  };

  const commitText = () => {
    const parsed = parseTime(text);
    if (parsed !== null && parsed !== value) {
      commit(parsed);
    } else {
      // Invalid or unchanged: restore the last valid value.
      setText(value !== undefined ? formatTime(value) : "");
    }
  };

  const pick = (total: number) => {
    commit(total);
    setOpen(false);
    inputRef.current?.focus();
  };

  const moveHighlight = (dir: 1 | -1) => {
    if (!slots.length) return;
    const idx = nearestSlot !== undefined ? slots.indexOf(nearestSlot) : -1;
    const next = slots[Math.min(slots.length - 1, Math.max(0, idx + dir))];
    setText(formatTime(next));
    setOpen(true);
  };

  return (
    <div className={cn("relative w-full", className)}>
      <Popover open={open && !disabled} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div
            className={cn(
              "flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm transition-colors",
              "focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary",
              disabled && "cursor-not-allowed opacity-50"
            )}
            onClick={() => {
              if (disabled) return;
              inputRef.current?.focus();
              setOpen(true);
            }}
          >
            <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              id={triggerId}
              data-testid={triggerId}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              dir="ltr"
              disabled={disabled}
              value={text}
              placeholder={placeholder || t("selectTime", { defaultValue: "Choisir l'heure" })}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setText(e.target.value);
                setOpen(true);
              }}
              onBlur={commitText}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitText();
                  setOpen(false);
                } else if (e.key === "Escape") {
                  setText(value !== undefined ? formatTime(value) : "");
                  setOpen(false);
                } else if (e.key === "ArrowDown") {
                  e.preventDefault();
                  moveHighlight(1);
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  moveHighlight(-1);
                }
              }}
              className="w-full min-w-0 bg-transparent font-mono tabular-nums outline-none placeholder:font-sans placeholder:text-muted-foreground disabled:cursor-not-allowed"
            />
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-[var(--radix-popover-trigger-width)] min-w-44 p-1"
          // Keep focus in the input so the user can keep typing.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (e.target instanceof Node && inputRef.current?.parentElement?.contains(e.target)) {
              e.preventDefault();
            }
          }}
        >
          <div ref={listRef} role="listbox" className="max-h-60 overflow-y-auto" dir="ltr">
            {slots.map((s) => {
              const selected = s === value;
              const highlighted = s === nearestSlot && !selected;
              return (
                <button
                  key={s}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-slot={s}
                  // mousedown fires before the input blur, so prevent it to avoid a double commit.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(s)}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 rounded-sm px-2.5 py-1.5 text-sm transition-colors",
                    selected
                      ? "bg-primary text-primary-foreground font-medium"
                      : highlighted
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <span className="font-mono tabular-nums">{formatTime(s)}</span>
                  {ref !== undefined && s > ref && (
                    <span
                      className={cn(
                        "text-xs",
                        selected ? "text-primary-foreground/80" : "text-muted-foreground"
                      )}
                    >
                      {formatDuration(s - ref)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
